import {
  CellStyleModule,
  ClientSideRowModelModule,
  type ICellRendererParams,
  ModuleRegistry,
  NumberEditorModule,
  NumberFilterModule,
  PaginationModule,
  QuickFilterModule,
  RowApiModule,
  RowDragModule,
  RowSelectionModule,
  type RowSelectionOptions,
  TextEditorModule,
  TextFilterModule,
  themeAlpine,
} from 'ag-grid-community';
import { AgGridReact, type AgGridReactProps } from 'ag-grid-react';
import { capitalCase } from 'case-anything';
import { type FC, type ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { Button, Col, Image, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Container from 'react-bootstrap/Container';
import {
  statusColorMapper,
  statusTextColorMapper,
} from '../../../modules/mappers/statusColorMapper';
import { CurrencyInput } from '../../Inputs/Currency';
import './styles/styles.css';
import { Can } from '../../../modules/utils/can';
import { SearchTableInput } from '../../Inputs/Search';

export interface BaseGridProps<T = any> extends AgGridReactProps<T> {
  refreshHandler?: (token: string) => Promise<void>;
  searchString?: string;
  changeState: number;
  showSearchInput?: boolean;
  addButtonFunction?: () => void;
  buttonFunction?: () => void;
  secondaryButtonFunction?: () => void;
  addButtonText?: string;
  buttonContent?: ReactNode;
  secondaryButtonContent?: ReactNode;
  leftSideContent?: ReactNode;
  component?: string;
  rowSelect?: boolean;
  onSelection?: (selectedData: any[]) => void;
}

export const CurrencyCellRenderer: FC<ICellRendererParams> = (
  params: ICellRendererParams,
) => {
  const { value } = params;

  return <CurrencyInput displayType="text" value={value} />;
};

export const ImageLabelCellRenderer: FC<ICellRendererParams> = (
  params: ICellRendererParams,
) => {
  const { value } = params;
  return (
    <>
      <Image alt="" src={value.img} width="40" height="40" className="ag-cell-img" />
      <span>{value.name}</span>
    </>
  );
};

export const BadgeCellRenderer: FC<ICellRendererParams> = (
  params: ICellRendererParams,
) => {
  const { value } = params;

  return (
    <div
      className="status-badge"
      style={{
        backgroundColor: statusColorMapper[value.toUpperCase() as string],
        color: statusTextColorMapper[value.toUpperCase() as string],
      }}
    >
      {capitalCase(value)}
    </div>
  );
};

ModuleRegistry.registerModules([
  RowDragModule,
  RowApiModule,
  NumberEditorModule,
  TextEditorModule,
  TextFilterModule,
  NumberFilterModule,
  CellStyleModule,
  PaginationModule,
  ClientSideRowModelModule,
  QuickFilterModule,
  RowSelectionModule,
]);

export const TextWithTooltipCellRenderer: FC<ICellRendererParams> = (
  params: ICellRendererParams,
) => {
  const { value = '-' } = params;
  return (
    <OverlayTrigger
      overlay={
        <Tooltip>
          <strong>{value}</strong>
        </Tooltip>
      }
    >
      <div>{value}</div>
    </OverlayTrigger>
  );
};

export const DataTable: FC<BaseGridProps> = <T,>(props: BaseGridProps<T>) => {
  const {
    rowData,
    refreshHandler,
    paginationPageSize = 1,
    searchString,
    addButtonFunction,
    addButtonText,
    buttonFunction,
    buttonContent,
    changeState,
    pagination,
    rowDragManaged = false,
    rowDragEntireRow = false,
    onRowDragEnd,
    showSearchInput = true,
    component,
    rowSelect,
    leftSideContent,
    onSelection,
    secondaryButtonFunction,
    secondaryButtonContent,
    ...rest
  } = props;
  const gridRef = useRef<AgGridReact>(null);
  const memoizedData = useMemo(() => rowData, [rowData]);

  const [quickFilter, setQuickFilter] = useState('');

  const handleQuickFilter = useCallback((value: string) => {
    setQuickFilter(value);
    gridRef.current?.api.setGridOption('quickFilterText', value);
  }, []);

  const rowSelection = useMemo<RowSelectionOptions | 'single' | 'multiple'>(() => {
    return {
      mode: 'multiRow',
    };
  }, []);

  const rowHeight = 54;

  const onSelectionChanged = () => {
    if (gridRef.current && gridRef.current.api) {
      const selectedData = gridRef.current.api.getSelectedRows();

      if (onSelection) {
        onSelection(selectedData);
      }
    }
  };

  return (
    <Container fluid>
      <Container
        className="table-header"
        style={{ display: 'flex', justifyContent: 'end', marginBottom: '15px' }}
        fluid
      >
        {leftSideContent && <Col>{leftSideContent}</Col>}
        <Col style={{ display: 'flex', justifyContent: 'right' }}>
          {showSearchInput && (
            <SearchTableInput
              value={quickFilter}
              style={{ width: '370px' }}
              placeholder="Search here"
              onInput={(e) => handleQuickFilter(e.currentTarget.value)}
            />
          )}
          {addButtonFunction && addButtonText && (
            <Can I="create" a={component || ''}>
              <Button
                id="dropdown-basic-button"
                className="w-auto table-add-button"
                style={{ marginLeft: '15px' }}
                onClick={addButtonFunction}
              >
                <Image alt="" src="/plus.svg" />
                {addButtonText}
              </Button>
            </Can>
          )}
        </Col>

        {buttonFunction && buttonContent && (
          <Button
            id="dropdown-basic-button"
            className="w-auto table-add-button"
            style={{ marginLeft: '15px' }}
            onClick={buttonFunction}
          >
            {buttonContent}
          </Button>
        )}
        {secondaryButtonFunction && secondaryButtonContent && (
          <Button
            id="dropdown-secondary-button"
            className="w-auto table-add-button"
            style={{ marginLeft: '15px' }}
            onClick={secondaryButtonFunction}
          >
            {secondaryButtonContent}
          </Button>
        )}
      </Container>

      <AgGridReact
        ref={gridRef}
        theme={themeAlpine}
        rowDragManaged={true}
        animateRows={true}
        onRowDragEnd={onRowDragEnd}
        rowHeight={rowHeight}
        rowData={memoizedData}
        pagination={!!pagination}
        paginationPageSize={paginationPageSize}
        domLayout="autoHeight"
        rowSelection={rowSelect ? rowSelection : undefined}
        onSelectionChanged={onSelection ? onSelectionChanged : undefined}
        rowDragEntireRow={rowDragEntireRow}
        {...rest}
      />
    </Container>
  );
};
